import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Clock3,
  Mail,
  MapPin,
  Phone,
  Shield,
  Star,
  Zap,
  Edit,
  Loader,
  Trophy,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Linkedin,
  Github,
  Globe,
  Award,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getTaskerProfileByUserId } from '../api/taskerProfiles';
import { TaskerProfile as TaskerProfileType } from '../types';
import { computeUserRank, UserRankData } from '../api/ranking';
import RankBadge from '../components/RankBadge';

const TaskerProfile: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<TaskerProfileType | null>(null);
  const [loading, setLoading] = useState(true);
  const [rankData, setRankData] = useState<UserRankData | null>(null);
  const [rankLoading, setRankLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      setRankLoading(true);
      computeUserRank(currentUser.uid)
        .then(setRankData)
        .catch((err) => console.error('Error computing rank:', err))
        .finally(() => setRankLoading(false));
    }
  }, [currentUser]);

  const loadProfile = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await getTaskerProfileByUserId(currentUser.uid);
      setProfile(data);
    } catch (error: any) {
      console.error('Error loading profile in TaskerProfile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShareProfile = () => {
    const url = `${window.location.origin}/tasker/${currentUser?.uid}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tech-dark flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-10 h-10 text-tech-cyan animate-spin mx-auto mb-3" />
          <p className="text-tech-text-muted">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Profile data with fallbacks
  const displayName =
    profile?.personalInfo.displayName ||
    currentUser?.displayName ||
    currentUser?.email?.split('@')[0] ||
    'Tasker';
  const email = profile?.userEmail || currentUser?.email || 'tasker@example.com';
  const avatarInitial = displayName.charAt(0).toUpperCase();
  const specialties = profile?.specialties || [];

  const getStatusColors = () => {
    if (!profile) return 'bg-yellow-500/10 border-yellow-400/30 text-yellow-200';
    const statusColors: Record<string, string> = {
      incomplete: 'bg-yellow-500/10 border-yellow-400/30 text-yellow-200',
      pending: 'bg-blue-500/10 border-blue-400/30 text-blue-200',
      approved: 'bg-green-500/10 border-green-400/30 text-green-200',
      active: 'bg-green-500/10 border-green-400/30 text-green-200',
      suspended: 'bg-red-500/10 border-red-400/30 text-red-200',
    };
    return statusColors[profile.status] || 'bg-gray-500/10 border-gray-400/30 text-gray-200';
  };

  const rankGlow = rankData ? rankData.rank.glowColor : 'rgba(34,211,238,0.3)';

  return (
    <div className="min-h-screen bg-tech-dark text-tech-text relative overflow-hidden">
      {/* Ambient glows */}
      <div className="fixed inset-0 pointer-events-none opacity-40 z-0">
        <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-tech-cyan/10 rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10">
        {/* ── Hero Section ── */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-tech-cyan/10 via-blue-500/5 to-purple-500/10" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(34,211,238,0.12),transparent_70%)]" />

          <div className="relative max-w-6xl mx-auto px-4 py-12">
            <div className="flex flex-col md:flex-row md:items-start gap-8">
              {/* Avatar */}
              <div className="shrink-0 relative">
                <div
                  className="w-28 h-28 rounded-3xl bg-gradient-to-br from-tech-cyan to-blue-600 flex items-center justify-center text-4xl font-bold text-white"
                  style={{ boxShadow: `0 0 40px ${rankGlow}, 0 0 80px ${rankGlow}` }}
                >
                  {avatarInitial}
                </div>
                {rankData && (
                  <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
                    <RankBadge rankData={rankData} size="sm" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 mt-4 md:mt-0">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <span className="text-sm text-tech-text-muted">My Profile</span>
                  {profile && (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs border capitalize ${getStatusColors()}`}>
                      {profile.status === 'incomplete' && <AlertCircle className="w-3 h-3" />}
                      {profile.status === 'pending' && <Loader className="w-3 h-3 animate-spin" />}
                      {(profile.status === 'approved' || profile.status === 'active') && <CheckCircle2 className="w-3 h-3" />}
                      {profile.status === 'suspended' && <XCircle className="w-3 h-3" />}
                      {profile.status}
                    </span>
                  )}
                  {profile?.completionPercentage != null && (
                    <span className="text-xs text-tech-cyan font-semibold px-2 py-0.5 rounded-full bg-tech-cyan/10 border border-tech-cyan/20">
                      {profile.completionPercentage}% Complete
                    </span>
                  )}
                </div>

                <h1 className="text-3xl md:text-5xl font-bold mb-2 animated-gradient-text">{displayName}</h1>
                {profile?.professionalInfo.title && (
                  <p className="text-tech-text-muted text-lg mb-3">{profile.professionalInfo.title}</p>
                )}

                <div className="flex flex-wrap items-center gap-3 text-sm text-tech-text-muted mb-6">
                  {profile?.personalInfo.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {profile.personalInfo.location}
                    </span>
                  )}
                  {profile?.workPreferences.location && (
                    <span className="inline-flex items-center gap-1 capitalize">
                      <Globe className="w-4 h-4" />
                      {profile.workPreferences.location.replace('-', ' ')}
                    </span>
                  )}
                  {profile?.status === 'active' && (
                    <span className="inline-flex items-center gap-1 text-green-400">
                      <Shield className="w-4 h-4" />
                      Verified
                    </span>
                  )}
                  {profile?.workPreferences.fastTrackAvailable && (
                    <span className="inline-flex items-center gap-1 text-amber-400">
                      <Zap className="w-4 h-4" />
                      Fast-track available
                    </span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleShareProfile}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-tech-blue to-tech-cyan text-white font-semibold text-sm hover:opacity-90 transition-opacity"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Link Copied!' : 'Share Profile'}
                  </button>
                  {profile && (
                    <a
                      href={`/tasker/${currentUser?.uid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-tech-cyan/30 bg-tech-cyan/5 text-tech-cyan font-semibold text-sm hover:bg-tech-cyan/10 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View Public Profile
                    </a>
                  )}
                  <button
                    onClick={() => navigate('/tasker-meta')}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 bg-white/5 text-tech-text-muted font-semibold text-sm hover:bg-white/10 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                    {profile ? 'Edit Profile' : 'Complete Profile'}
                  </button>
                  {profile?.personalInfo.phone && (
                    <a
                      href={`tel:${profile.personalInfo.phone}`}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-tech-text-muted font-semibold text-sm hover:bg-white/10 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      {profile.personalInfo.phone}
                    </a>
                  )}
                  <a
                    href={`mailto:${email}`}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/10 bg-white/5 text-tech-text-muted font-semibold text-sm hover:bg-white/10 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    {email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Stats Grid ── */}
        {rankData && (
          <div className="max-w-6xl mx-auto px-4 -mt-2 mb-8">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Total Tasks', value: rankData.taskCount, icon: Briefcase, color: 'text-cyan-400' },
                { label: 'Completed', value: rankData.completedTaskCount, icon: CheckCircle2, color: 'text-green-400' },
                { label: 'Prompts Posted', value: rankData.promptCount, icon: Zap, color: 'text-violet-400' },
                { label: 'Rank Score', value: rankData.score.toLocaleString(), icon: Trophy, color: 'text-amber-400' },
              ].map((stat) => (
                <div key={stat.label} className="p-5 rounded-2xl glass-panel border border-white/8 text-center">
                  <stat.icon className={`w-5 h-5 ${stat.color} mx-auto mb-2`} />
                  <div className="text-2xl font-bold text-tech-text mb-0.5">{stat.value}</div>
                  <div className="text-xs text-tech-text-muted">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── No Profile Warning ── */}
        {!profile && (
          <div className="max-w-6xl mx-auto px-4 mb-8">
            <div className="p-6 rounded-2xl bg-yellow-500/10 border border-yellow-400/30">
              <div className="flex items-center gap-3 mb-2">
                <AlertCircle className="w-6 h-6 text-yellow-400 shrink-0" />
                <h3 className="text-lg font-semibold text-yellow-200">Profile Not Started</h3>
              </div>
              <p className="text-yellow-200/80 mb-4">
                You haven't created your tasker profile yet. Complete your profile to start getting matched with tasks.
              </p>
              <button
                onClick={() => navigate('/tasker-meta')}
                className="px-6 py-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-400/30 transition-all text-yellow-200"
              >
                Get Started
              </button>
            </div>
          </div>
        )}

        {/* ── Main Content Grid ── */}
        <div className="max-w-6xl mx-auto px-4 pb-16">
          <div className="grid gap-6 md:grid-cols-3">
            <div className="md:col-span-2 space-y-6">
              {/* Rank Card */}
              {rankLoading && (
                <div className="p-6 rounded-3xl glass-panel border border-tech-cyan/20 flex items-center gap-3">
                  <Loader className="w-5 h-5 text-tech-cyan animate-spin" />
                  <span className="text-tech-text-muted text-sm">Loading rank data...</span>
                </div>
              )}
              {rankData && !rankLoading && (
                <div
                  className={`p-6 rounded-3xl bg-gradient-to-br ${rankData.rank.color} border border-white/10 glass-panel`}
                  style={{ boxShadow: `0 0 40px ${rankData.rank.glowColor}` }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Trophy className="w-5 h-5 text-amber-400" />
                    <h2 className="text-lg font-semibold">Rank &amp; Standing</h2>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-5xl">{rankData.rank.emoji}</span>
                      <div>
                        <div className="text-2xl font-bold text-tech-text">{rankData.rank.title}</div>
                        <div className="text-sm text-tech-text-muted italic">"{rankData.rank.description}"</div>
                      </div>
                    </div>
                    <div className="sm:ml-auto text-right">
                      <div className="text-3xl font-bold text-tech-cyan">{rankData.score.toLocaleString()}</div>
                      <div className="text-xs text-tech-text-muted">total points</div>
                    </div>
                  </div>

                  {rankData.nextRank && (
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-1.5 text-sm">
                        <span className="text-tech-text-muted">Progress to {rankData.nextRank.emoji} {rankData.nextRank.title}</span>
                        <span className="text-tech-cyan font-semibold">{rankData.progressToNext}%</span>
                      </div>
                      <div className="w-full h-2.5 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-tech-cyan to-blue-400 transition-all duration-500"
                          style={{ width: `${rankData.progressToNext}%` }}
                        />
                      </div>
                      <div className="text-xs text-tech-text-muted mt-1">
                        {rankData.nextRank.minScore.toLocaleString()} pts needed · {(rankData.nextRank.minScore - rankData.score).toLocaleString()} pts to go
                      </div>
                    </div>
                  )}
                  {!rankData.nextRank && (
                    <div className="p-3 rounded-xl bg-amber-400/10 border border-amber-400/30 text-amber-300 text-sm font-semibold text-center">
                      🌐 Maximum Rank Achieved — You are a Visionary!
                    </div>
                  )}

                  {/* Earned Badges */}
                  {rankData.earnedBadges.length > 0 && (
                    <div>
                      <p className="text-xs text-tech-text-muted mb-2 uppercase tracking-wider font-semibold">Earned Badges</p>
                      <div className="flex flex-wrap gap-2">
                        {rankData.earnedBadges.map((badge) => (
                          <span
                            key={badge.id}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border bg-gradient-to-br ${badge.color}`}
                            title={badge.description}
                          >
                            {badge.emoji} {badge.title}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {rankData.earnedBadges.length === 0 && (
                    <p className="text-xs text-tech-text-muted">No badges yet — keep creating and posting to unlock them!</p>
                  )}
                </div>
              )}

              {/* Bio */}
              {profile?.personalInfo.bio && (
                <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                  <h2 className="text-lg font-semibold mb-3">Profile Overview</h2>
                  {profile.workPreferences.fastTrackAvailable && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-amber-500/10 border border-amber-400/20 text-amber-200 mb-3">
                      <Zap className="w-3.5 h-3.5" />
                      Available for fast-track tasks
                    </span>
                  )}
                  <p className="text-sm text-tech-text-muted leading-relaxed whitespace-pre-line">
                    {profile.personalInfo.bio}
                  </p>
                </div>
              )}

              {/* Skills */}
              {(profile?.skills.primary.length || profile?.skills.secondary.length) ? (
                <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                  <h3 className="text-lg font-semibold mb-3">Skills &amp; Expertise</h3>
                  {(profile?.skills.primary ?? []).length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-tech-text-muted mb-2 uppercase tracking-wider">Primary Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {(profile?.skills.primary ?? []).map((skill) => (
                          <span key={skill} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-tech-cyan/10 border border-tech-cyan/20 text-tech-cyan text-sm">
                            <Star className="w-3 h-3" />
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {(profile?.skills.secondary ?? []).length > 0 && (
                    <div>
                      <p className="text-xs text-tech-text-muted mb-2 uppercase tracking-wider">Secondary Skills</p>
                      <div className="flex flex-wrap gap-2">
                        {(profile?.skills.secondary ?? []).map((skill) => (
                          <span key={skill} className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-500/10 border border-indigo-400/20 text-indigo-300 text-sm">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Specialties */}
              {specialties.length > 0 && (
                <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                  <h3 className="text-lg font-semibold mb-3">Specialties</h3>
                  <div className="flex flex-wrap gap-3">
                    {specialties.map((item) => (
                      <span key={item} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-400/20 text-sm">
                        <Star className="w-4 h-4 text-violet-400" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Achievements */}
              {profile?.portfolio.achievements && profile.portfolio.achievements.length > 0 && (
                <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                  <h3 className="text-lg font-semibold mb-3">Key Achievements</h3>
                  <div className="space-y-4">
                    {profile.portfolio.achievements.slice(0, 3).map((achievement) => (
                      <div key={achievement.id} className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-400/20 flex items-center justify-center shrink-0">
                          <Trophy className="w-4 h-4 text-amber-400" />
                        </div>
                        <div>
                          <p className="font-medium text-tech-text">{achievement.title}</p>
                          <p className="text-sm text-tech-text-muted">{achievement.description}</p>
                          {achievement.metrics && (
                            <p className="text-xs text-tech-text-muted/60 mt-1">{achievement.metrics}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Social Links */}
              {(profile?.professionalInfo.linkedInUrl || profile?.professionalInfo.githubUrl || profile?.professionalInfo.portfolioUrl) && (
                <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                  <h3 className="text-lg font-semibold mb-3">Links</h3>
                  <div className="flex flex-wrap gap-3">
                    {profile.professionalInfo.linkedInUrl && (
                      <a href={profile.professionalInfo.linkedInUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 border border-blue-400/30 transition-all text-sm">
                        <Linkedin className="w-4 h-4" /> LinkedIn
                      </a>
                    )}
                    {profile.professionalInfo.githubUrl && (
                      <a href={profile.professionalInfo.githubUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-500/10 hover:bg-gray-500/20 border border-gray-400/30 transition-all text-sm">
                        <Github className="w-4 h-4" /> GitHub
                      </a>
                    )}
                    {profile.professionalInfo.portfolioUrl && (
                      <a href={profile.professionalInfo.portfolioUrl} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 border border-purple-400/30 transition-all text-sm">
                        <Globe className="w-4 h-4" /> Portfolio
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar */}
            <div className="space-y-6">
              {/* Work Preferences */}
              {profile && (
                <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                  <h3 className="text-lg font-semibold mb-3">Work Preferences</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-tech-text-muted">Availability</span>
                      <span className="capitalize text-tech-text">{profile.workPreferences.availability.replace('-', ' ')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-tech-text-muted">Location</span>
                      <span className="capitalize text-tech-text">{profile.workPreferences.location.replace('-', ' ')}</span>
                    </div>
                    {profile.workPreferences.hourlyRate && (
                      <div className="flex items-center justify-between">
                        <span className="text-tech-text-muted">Hourly Rate</span>
                        <span className="text-tech-text">{profile.workPreferences.currency || 'USD'} {profile.workPreferences.hourlyRate}</span>
                      </div>
                    )}
                    {profile.workPreferences.preferredHoursPerWeek && (
                      <div className="flex items-center justify-between">
                        <span className="text-tech-text-muted">Hours/Week</span>
                        <span className="text-tech-text">{profile.workPreferences.preferredHoursPerWeek}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Metrics */}
              <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                <h3 className="text-lg font-semibold mb-4">Key Metrics</h3>
                <div className="grid grid-cols-2 gap-3">
                  {profile ? (
                    [
                      { label: 'Tasks delivered', value: profile.metrics.tasksCompleted.toString(), icon: Briefcase },
                      { label: 'SLA reliability', value: `${profile.metrics.slaReliability}%`, icon: Shield },
                      { label: 'Avg. turnaround', value: `${profile.metrics.averageTurnaroundHours.toFixed(1)}h`, icon: Clock3 },
                      { label: 'Quality score', value: `${profile.metrics.qualityScore.toFixed(1)}/5`, icon: Star },
                    ].map((item) => (
                      <div key={item.label} className="p-4 rounded-xl bg-white/5 border border-white/8 flex flex-col gap-2">
                        <item.icon className="w-4 h-4 text-tech-cyan" />
                        <span className="text-lg font-semibold text-tech-text">{item.value}</span>
                        <span className="text-xs text-tech-text-muted">{item.label}</span>
                      </div>
                    ))
                  ) : (
                    [
                      { label: 'Tasks delivered', value: '0', icon: Briefcase },
                      { label: 'SLA reliability', value: 'N/A', icon: Shield },
                      { label: 'Avg. turnaround', value: 'N/A', icon: Clock3 },
                      { label: 'Quality score', value: 'N/A', icon: Star },
                    ].map((item) => (
                      <div key={item.label} className="p-4 rounded-xl bg-white/5 border border-white/8 flex flex-col gap-2">
                        <item.icon className="w-4 h-4 text-tech-text-muted" />
                        <span className="text-lg font-semibold text-tech-text-muted">{item.value}</span>
                        <span className="text-xs text-tech-text-muted">{item.label}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* TaskAchiever Status */}
              {profile && (
                <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                  <div className="flex items-center gap-3 mb-4">
                    <Trophy className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-semibold">TaskAchiever Status</h3>
                  </div>
                  {profile.isTaskAchieverEligible ? (
                    <div className="space-y-3">
                      <div className="p-3 rounded-lg bg-green-500/10 border border-green-400/30">
                        <div className="flex items-center gap-2 mb-1">
                          <CheckCircle2 className="w-5 h-5 text-green-400" />
                          <span className="font-medium text-green-200">You're Eligible!</span>
                        </div>
                        <p className="text-sm text-green-200/80">
                          You meet all criteria to become a TaskAchiever. Apply to unlock premium features.
                        </p>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-tech-text-muted">Status</span>
                          <span className="capitalize text-tech-text">{profile.taskAchieverStatus.replace('-', ' ')}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-tech-text-muted">Tasks Completed</span>
                          <span className="text-tech-text">{profile.metrics.tasksCompleted} / {profile.taskAchieverCriteria.minimumTasksCompleted}</span>
                        </div>
                        {profile.metrics.averageRating > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-tech-text-muted">Rating</span>
                            <span className="text-tech-text">{profile.metrics.averageRating.toFixed(1)} / {profile.taskAchieverCriteria.minimumRating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-400/30">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-yellow-400" />
                        <span className="font-medium text-yellow-200 text-sm">Work in Progress</span>
                      </div>
                      <ul className="space-y-1 text-xs text-yellow-200/70">
                        {profile.taskAchieverCriteria.minimumTasksCompleted > profile.metrics.tasksCompleted && (
                          <li className="flex items-center gap-2">
                            <XCircle className="w-3 h-3 shrink-0" />
                            Complete {profile.taskAchieverCriteria.minimumTasksCompleted} tasks (currently: {profile.metrics.tasksCompleted})
                          </li>
                        )}
                        {profile.completionPercentage < 90 && (
                          <li className="flex items-center gap-2">
                            <XCircle className="w-3 h-3 shrink-0" />
                            Complete 90% of your profile (currently: {profile.completionPercentage}%)
                          </li>
                        )}
                        {profile.skills.primary.length < 3 && (
                          <li className="flex items-center gap-2">
                            <XCircle className="w-3 h-3 shrink-0" />
                            Add at least 3 primary skills
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Certifications */}
              {profile?.skills.certifications && profile.skills.certifications.length > 0 && (
                <div className="p-6 rounded-3xl glass-panel border border-white/8 premium-card-hover">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <Award className="w-5 h-5 text-tech-cyan" />
                    Certifications
                  </h3>
                  <div className="space-y-2">
                    {profile.skills.certifications.slice(0, 3).map((cert) => (
                      <div key={cert.id} className="p-3 rounded-lg bg-white/5 border border-white/8">
                        <div className="font-medium text-sm text-tech-text">{cert.name}</div>
                        <div className="text-xs text-tech-text-muted">{cert.issuer}</div>
                        {cert.credentialUrl && (
                          <a href={cert.credentialUrl} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-tech-cyan hover:text-tech-cyan/80 mt-1 inline-block">
                            Verify →
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskerProfile;
